import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Theme, Typography, Divider, Grid, Stack } from '@mui/material';
import createStyles from '@mui/styles/createStyles';
import makeStyles from '@mui/styles/makeStyles';
import {
	Shop,
	Shopper,
	OrderObject,
	OrderProduct,
	OrderDeliveryDetails,
	OrderDeliveryService,
	PurchaseTypes,
} from 'common/types';
import moment from 'moment-timezone';
import bwipjs from 'bwip-js';
import { getAddressStringFromLocation, getPricingString } from 'common/utils/common';
import classNames from 'classnames';
import {
	getEarliestProductStartTime,
	getDurationString,
	getTotalRentalDurationFromProducts,
	getLatestProductEndTime,
} from 'common/utils/dateCalculations';
import ReceiptProductInfo from './ReceiptProductInfo';
import { getCurrencyObjectFromCurrencyCode } from 'common/utils/currencyUtils';
import { getResponsiblePersonDetails } from 'common/utils/newRentalUtils';
import useShopFormat from 'hooks/useShopFormat';
import { useTranslation } from 'services/localization/useTranslation';
import { getTranslation } from 'common/modules/translations';
import errorHandler from 'common/services/errorHandler';
import { hasSkidataConfirmationNumber, isLiftTicketProduct } from 'common/utils/liftTicketUtils';

import { getOrderPricing, getPricingOptions, getTotalProductPricing } from 'common/modules/orders';
import { PriceBreakdown } from 'common/components/molecules/PriceBreakdown';
import { getOrderPayments } from 'common/modules/atoms/paymentAmounts';
import { getShopReceiptInfo } from 'common/utils/shopUtils';
import { getFormattedAddress } from 'common/modules/atoms/addresses';
import { timeslotToString } from 'common/modules/delivery/utils';

import * as ViewSelectors from 'selectors/ViewSelectors';
import * as ShopSelectors from 'selectors/ShopSelectors';
import RentleLogo from 'common/components/RentleLogo';

export interface Props {
	shop: Shop;
	rental: OrderObject;
	ref: React.MutableRefObject<any>;
	barCode: string;
}

const Receipt = (props: Props) => {
	const classes = useStyles();
	const { t } = useTranslation();
	const { localFormat, shopDateFormat } = useShopFormat();
	const { rental, shop } = props;
	const barCode = props.barCode.trim();
	const { publicInfo } = shop;
	const { rentalInfo, products, shoppers, orderDelivery } = rental;
	const currency = getCurrencyObjectFromCurrencyCode(
		rentalInfo.pricing.currency,
		publicInfo.currency,
	);
	const lang = useSelector(ViewSelectors.lang);
	const timezone = useSelector(ShopSelectors.getShopTimeZone);
	const totalPricing = getOrderPricing(
		products,
		orderDelivery,
		getPricingOptions(rentalInfo.pricing),
	);
	const shopReceiptInfo = getShopReceiptInfo(publicInfo, rentalInfo.startLocation.id);
	const hasSkidataConfirmation = hasSkidataConfirmationNumber(rentalInfo);

	useEffect(() => {
		const createBarCode = async () => {
			if (rentalInfo.id) {
				try {
					(bwipjs as any).toCanvas(`barcode_${rentalInfo.id}`, {
						bcid: 'code128',
						text: barCode,
						scale: 3,
						height: 10,
						includetext: false,
						textxalign: 'center',
					});
				} catch (e) {
					errorHandler.report(e);
				}
			}
		};
		createBarCode();
	}, [barCode, rentalInfo.id]);

	const renderProductSection = (product: OrderProduct) => {
		return (
			<React.Fragment key={product.id}>
				<Grid
					key={product.id}
					className={product.set ? undefined : classes.spaceBetween}
					container
					justifyContent="space-between"
				>
					<Grid item xs className={classes.productFlex}>
						<ReceiptProductInfo product={product} />
					</Grid>
					<Grid item className={classes.noFlex} xs>
						<Typography className={classes.receiptFont} variant="body2">
							{getPricingString(product.pricing.listPrice, currency)}
						</Typography>
					</Grid>
				</Grid>
				{product.set &&
					product.setProducts.map((setProduct, index) => {
						return (
							<Grid
								key={'setReceipt' + index}
								className={index === product.setProducts.length - 1 ? classes.spaceSet : undefined}
							>
								<Grid item xs>
									<ReceiptProductInfo product={setProduct} setProduct={true} />
								</Grid>
							</Grid>
						);
					})}
			</React.Fragment>
		);
	};

	const renderDeliveryPricing = (deliveryInformation: OrderDeliveryService) => {
		const deliveryCostsTo = deliveryInformation.to?.pricing?.subtotal ?? 0;
		const deliveryCostsFrom = deliveryInformation.from?.pricing?.subtotal ?? 0;
		return (
			<>
				{deliveryInformation.to && (
					<Grid container justifyContent="space-between">
						<Grid item xs>
							<Typography className={classes.receiptFont} variant="body2" align="left">
								{t('receipt.delivery', 'Delivery')}
							</Typography>
						</Grid>
						<Grid item className={classes.noFlex}>
							<Typography className={classes.receiptFont} variant="body2" align="right">
								{getPricingString(deliveryCostsTo, currency)}
							</Typography>
						</Grid>
					</Grid>
				)}
				{deliveryInformation.from && (
					<Grid container justifyContent="space-between">
						<Grid item xs>
							<Typography className={classes.receiptFont} variant="body2" align="left">
								{t('receipt.pickup', 'Pickup')}
							</Typography>
						</Grid>
						<Grid item className={classes.noFlex}>
							<Typography className={classes.receiptFont} variant="body2" align="right">
								{getPricingString(deliveryCostsFrom, currency)}
							</Typography>
						</Grid>
					</Grid>
				)}
			</>
		);
	};

	const renderConfirmationNumber = () => (
		<Typography
			className={classNames(classes.receiptFont, classes.spacingTopSmall)}
			variant="body2"
		>
			<b>{`${t('skidata.skidataConfirmationNumber', 'Skidata confirmation number')} ${
				rentalInfo.skidataProps?.confirmationNumber
			}`}</b>
		</Typography>
	);

	const renderDeliverySection = (deliveryDetails: OrderDeliveryDetails, way: 'to' | 'from') => {
		const dateString = timeslotToString({
			timeslot: deliveryDetails.timeslot,
			shopDateFormat,
			timezone,
		});

		return (
			<div key={1} className={classes.spacingTopSmall}>
				<Typography className={classNames(classes.receiptFont, classes.upperCase)} variant="body2">
					<b>{way === 'to' ? t('receipt.delivery', 'Delivery') : t('receipt.pickup', 'Pickup')}</b>
				</Typography>
				<Typography className={classes.receiptFont} variant="body2">
					{t('receipt.carrier', 'Carrier')}:{' '}
					{deliveryDetails.carrier?.name
						? getTranslation(deliveryDetails.carrier.name, lang)
						: t('receipt.notAssigned', 'Not assigned')}
				</Typography>
				<Typography className={classes.receiptFont} variant="body2">
					{t('receipt.time', 'Time')}: {dateString}
				</Typography>
				{deliveryDetails.location?.address && (
					<>
						<Typography className={classes.receiptFont} variant="body2">
							{getFormattedAddress(deliveryDetails.location?.address)}
						</Typography>
						{!!deliveryDetails.location.address?.details && (
							<Typography className={classes.receiptFont} variant="body2">
								{deliveryDetails.location.address?.details}
							</Typography>
						)}
					</>
				)}
			</div>
		);
	};

	const renderResponsiblePersonSection = () => {
		const responsiblePerson = getResponsiblePersonDetails(rentalInfo.responsiblePerson, shoppers);
		if (!responsiblePerson) {
			return;
		}
		const responsiblePersonName = responsiblePerson.firstName + ' ' + responsiblePerson.lastName;
		return (
			<div key={responsiblePerson.id} className={classes.spacingTopSmall}>
				<Typography className={classes.receiptFont} variant="body2">
					<b>{t('receipt.contactInformation', 'Contact information')}</b>
				</Typography>
				<Typography className={classes.receiptFont} variant="body2">
					{responsiblePersonName}
				</Typography>
				<Typography className={classes.receiptFont} variant="body2">
					{responsiblePerson.email}
				</Typography>
				<Typography className={classes.receiptFont} variant="body2">
					{responsiblePerson.phone}
				</Typography>
				<Divider className={classes.dividerSolid} />
			</div>
		);
	};

	const renderCustomFieldSection = () => {
		const label = rentalInfo.bookingComment?.label;
		const value = rentalInfo.bookingComment?.value;

		if (!label || !value) return null;

		return (
			<div>
				<Typography
					className={classNames(classes.receiptFont, classes.spacingTopSmall)}
					variant="body2"
				>
					<b>{getTranslation(label, lang)}</b>
				</Typography>
				<Typography className={classes.receiptFont} variant="body2">
					{value}
				</Typography>
			</div>
		);
	};

	const renderShopperSection = (shopper: Shopper) => {
		const name = `${shopper.firstName} ${shopper.lastName}`;
		const shopperProducts = products.filter((p) => p.shopperId === shopper.id);
		const rentalProducts = shopperProducts.filter((p) => p.purchaseType === PurchaseTypes.rental);
		const salesProducts = shopperProducts.filter((p) => p.purchaseType === PurchaseTypes.sales);
		const startDate =
			getEarliestProductStartTime(shopperProducts) || rentalInfo.startDate || undefined;
		const endDate =
			getLatestProductEndTime(shopperProducts) || rentalInfo.endDateReturned || undefined;
		const rentalDuration = getTotalRentalDurationFromProducts(rentalProducts);
		const durationString = getDurationString(rentalDuration, 'long', lang, t);
		const sameDay = moment(startDate).isSame(moment(endDate), 'day');
		const isTicketsOnly = shopperProducts.every(isLiftTicketProduct);
		const dateString = isTicketsOnly
			? `${localFormat(startDate, 'ddd D.M.YYYY')}`
			: sameDay
			? `${localFormat(startDate, 'ddd D.M.YYYY HH:mm')} - ${localFormat(endDate, 'HH:mm')}`
			: `${localFormat(startDate, 'ddd D.M.YYYY HH:mm')} - ${localFormat(
					endDate,
					'ddd D.M.YYYY HH:mm',
			  )}`;
		const shopperPricing = getTotalProductPricing(shopperProducts);
		return (
			<div key={shopper.id} className={classes.spacingTopSmall}>
				<Typography className={classes.receiptFont} variant="body2">
					<b>{name}</b>
				</Typography>
				{rentalProducts.length > 0 && (
					<>
						<Typography className={classes.receiptFont} variant="body2">
							{durationString}
						</Typography>
						<Typography className={classes.receiptFont} variant="body2">
							{dateString}
						</Typography>
						<div className={classes.spacingTopXSmall}>
							{rentalProducts.map(renderProductSection)}
						</div>
					</>
				)}
				{salesProducts.length > 0 && (
					<>
						<Typography sx={{ pt: 3 }} className={classes.receiptFont} variant="body2">
							{t('common:states.sold', 'Sold')}
						</Typography>
						<div className={classes.spacingTopXSmall}>
							{salesProducts.map(renderProductSection)}
						</div>
					</>
				)}
				<PriceBreakdown
					pricing={shopperPricing}
					t={t}
					shopCurrency={currency}
					rows={['manualDiscount', 'discountCodes']}
					lang={lang}
				/>
				{shoppers.length > 1 && (
					<>
						<Divider
							classes={{
								root: classes.divider,
							}}
						/>
						<PriceBreakdown
							pricing={shopperPricing}
							shopCurrency={currency}
							t={t}
							rows={['subtotal']}
							lang={lang}
						/>
					</>
				)}
			</div>
		);
	};

	const renderFooter = () => {
		return (
			<div>
				<Stack alignItems="center">
					<RentleLogo width={60} type="black" />

					<Typography
						className={classNames(classes.receiptFont, classes.idCaption)}
						variant="body2"
						align="center"
					>
						{rentalInfo.id}
					</Typography>
				</Stack>
				<Grid container justifyContent="space-between" className={classes.spacingTopSmall}>
					<Grid item>
						<Typography className={classes.receiptFont} align="center" variant="caption">
							{'Powered by Rentle'}
						</Typography>
					</Grid>
					<Grid item>
						<Typography className={classes.receiptFont} align="center" variant="caption">
							{'www.rentle.io'}
						</Typography>
					</Grid>
				</Grid>
			</div>
		);
	};

	return (
		<div className={classes.receiptContainer}>
			<div>
				<Typography className={classes.receiptFont} align="center" variant="h6">
					<b>{t('receipt.orderConfirmation', 'Order confirmation')}</b>
				</Typography>
			</div>
			<div className={classes.spacingTopXSmall}>
				<Typography
					className={classNames(classes.receiptFont, classes.smFont)}
					variant="body1"
					align="center"
				>
					{shopReceiptInfo.name}
				</Typography>
				{shopReceiptInfo.visitingAddress && (
					<Typography
						className={classNames(classes.receiptFont, classes.smFont)}
						variant="body2"
						align="center"
					>
						{getAddressStringFromLocation(shopReceiptInfo.visitingAddress)}
					</Typography>
				)}
				{shopReceiptInfo.phone && (
					<Typography
						className={classNames(classes.receiptFont, classes.smFont)}
						variant="body2"
						align="center"
					>
						{shopReceiptInfo.phone}
					</Typography>
				)}
			</div>
			{hasSkidataConfirmation && renderConfirmationNumber()}
			{orderDelivery?.to && <div>{renderDeliverySection(orderDelivery.to, 'to')}</div>}
			{orderDelivery?.from && <div>{renderDeliverySection(orderDelivery.from, 'from')}</div>}
			<div>{renderResponsiblePersonSection()}</div>
			<div>{shoppers.map(renderShopperSection)}</div>
			<div>{renderCustomFieldSection()}</div>
			<div className={classes.spacingTopSmall}>
				<Divider className={classNames(classes.dividerSolid, classes.priceSectionDivider)} />
				<div>{!!orderDelivery && renderDeliveryPricing(orderDelivery)}</div>
				<PriceBreakdown
					pricing={totalPricing}
					payment={getOrderPayments(rentalInfo).charge}
					shopCurrency={currency}
					t={t}
					rows={['subtotal', 'taxes', 'total', 'paid']}
					lang={lang}
				/>
			</div>
			<div className={classNames(classes.spacingTopSmall, classes.textCenter)}>
				<canvas className={classes.barcode} id={`barcode_${rentalInfo.id}`}></canvas>
				<Typography
					variant="body2"
					gutterBottom
					className={classNames(classes.receiptFont, classes.smFont)}
					align="center"
				>
					{barCode}
				</Typography>
			</div>
			<div className={classes.spacingTopSmall}>{renderFooter()}</div>
		</div>
	);
};

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		receiptContainer: {
			width: '92%',
			maxWidth: 500,
			marginLeft: 'auto',
			marginRight: 'auto',
			padding: '4mm 2mm',
			'&*': {
				fontFamily: 'arial',
				'text-rendering': 'geometricPrecision',
				'-webkit-font-smoothing': 'subpixel-antialiased',
			},
			'& p': {
				margin: '2px 0',
			},
			color: 'black !important',
			fontWeight: 500,
			'text-rendering': 'geometricPrecision',
			'-webkit-font-smoothing': 'subpixel-antialiased',
		},
		spacingTopXSmall: {
			paddingTop: '8px',
		},
		spacingTopSmall: {
			paddingTop: '16px',
		},
		spacingTopLarge: {
			paddingTop: '32px',
		},
		receiptFont: {
			fontFamily: ['arial'].join(','),
			color: 'black !important',
			fontWeight: 500,
			fontSize: 14,
			lineHeight: 1.2,
			'text-rendering': 'geometricPrecision',
			'-webkit-font-smoothing': 'subpixel-antialiased',
		},
		smFont: {
			fontSize: 12,
		},
		idCaption: {
			paddingTop: 4,
			fontSize: 10,
		},
		noFlex: {
			flex: '0 0 auto',
			paddingLeft: 16,
		},
		dashedBorder: {
			paddingTop: 2,
			marginTop: 2,
			borderTop: '1px dashed black',
			width: 30,
			'text-rendering': 'geometricPrecision',
			'-webkit-font-smoothing': 'subpixel-antialiased',
		},
		divider: {
			border: '0 none',
			borderTop: '1px dashed black',
			background: 'none',
			height: 1,
			marginTop: '4px',
			paddingTop: '4px',
			'text-rendering': 'geometricPrecision',
			'-webkit-font-smoothing': 'subpixel-antialiased',
		},
		dividerSolid: {
			marginTop: theme.spacing(2),
			borderTop: '1px solid black',
		},
		priceSectionDivider: {
			margin: theme.spacing(0, 0, 2, 0),
		},
		paidSectionDivider: {
			marginBottom: theme.spacing(1),
		},
		textCenter: {
			textAlign: 'center',
		},
		barcode: {
			width: '100%',
			marginLeft: 'auto',
			marginRight: 'auto',
			maxWidth: 320,
			maxHeight: 48, // 15% of max-width
			padding: '0mm 2mm',
			color: theme.palette.common.black,
			'text-rendering': 'geometricPrecision',
			'-webkit-font-smoothing': 'subpixel-antialiased',
			marginBottom: 4,
		},
		spaceBetween: {
			marginBottom: '4px',
		},
		spaceSet: {
			marginBottom: theme.spacing(1),
		},
		productFlex: {
			flex: '1 1 0',
			display: 'flex',
			flexWrap: 'wrap',
			alignItems: 'baseline',
		},
		upperCase: {
			textTransform: 'uppercase',
		},
	}),
);

// Must export a class to work with react-to-print
class ReceiptContainer extends React.Component<Props> {
	render() {
		return (
			<div>
				<Receipt {...this.props} />
			</div>
		);
	}
}

export default ReceiptContainer;
